import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';

const CodeOfConduct: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-center mb-6">
          <Logo size="medium" showCompanyName={true} />
        </div>
        <div className="bg-white rounded-lg shadow-lg p-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:underline mb-6">
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to events
          </Link>

          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Star London Code of Conduct & Participation Policy
          </h1>

          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <div>
              <p className="text-lg font-medium mb-4">
                Star London is dedicated to providing a harassment-free experience for everyone, 
                regardless of age, gender, gender identity and expression, sexual orientation, 
                disability, physical appearance, body size, race, ethnicity, nationality, 
                religion, or professional background.
              </p>
            </div>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Our Standards</h2>
              
              <h3 className="text-xl font-semibold mb-3">Expected Behavior</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong>Professional Communication:</strong> All communication should be 
                  appropriate for a professional audience including people of diverse backgrounds. 
                  Sexual or offensive language and imagery is not appropriate at any time.
                </li>
                <li>
                  <strong>Kindness and Respect:</strong> Be kind, courteous, and respectful to 
                  all attendees, speakers, sponsors, and staff. Do not insult, harass, or put 
                  down other participants.
                </li>
                <li>
                  <strong>Inclusivity:</strong> Be mindful that some attendees may need assistance 
                  that is not immediately obvious. Star London is committed to maintaining an open, 
                  safe, friendly, and welcoming environment for everyone.
                </li>
                <li>
                  <strong>Collaboration:</strong> Focus on what is best for the community and the 
                  event. Be open to different viewpoints and experiences.
                </li>
                <li>
                  <strong>Venue Respect:</strong> Show full respect to the event space, venue staff, 
                  and facilities. Follow all venue rules and guidelines.
                </li>
              </ul>

              <h3 className="text-xl font-semibold mb-3 mt-6">Unacceptable Behavior</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Harassment of any kind, including but not limited to offensive verbal comments, 
                    deliberate intimidation, stalking, following, unwelcome photography or recording</li>
                <li>Inappropriate physical contact or unwelcome sexual attention</li>
                <li>Discriminatory language or actions based on the protected characteristics listed above</li>
                <li>Disruption of talks, workshops, or other events</li>
                <li>Advocating for, or encouraging, any of the above behaviors</li>
                <li>Excessive alcohol consumption or use of illegal substances</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Scope</h2>
              <p>
                This Code of Conduct applies to all Star London spaces, including:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-2">
                <li>The main event venue and all event activities</li>
                <li>Social events and gatherings associated with the event</li>
                <li>Online spaces related to the event (social media, chat platforms, etc.)</li>
                <li>One-on-one interactions between event participants</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Reporting</h2>
              <p className="mb-4">
                If you experience or witness unacceptable behavior, or have any other concerns, 
                please report it as soon as possible. You can report incidents:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>In person to any Star London staff member (identifiable by staff badges)</li>
                <li>By email to: <a href="mailto:events@star.london" className="text-blue-600 hover:underline">events@star.london</a></li>
                <li>Through a designated staff member if you prefer to remain anonymous</li>
              </ul>
              <p className="mt-4">
                All reports will be handled with complete confidentiality. Event staff will be 
                happy to help participants contact venue security or local law enforcement, 
                provide escorts, or otherwise assist those experiencing unacceptable behavior 
                to feel safe for the duration of the event.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Enforcement</h2>
              <p className="mb-4">
                Participants asked to stop any inappropriate behavior are expected to comply immediately.
              </p>
              <p className="mb-4">
                Event organizers may take any action they deem appropriate, including:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>A verbal warning to the offender</li>
                <li>Expulsion from the event</li>
                <li>Banning from future Star London events</li>
                <li>Reporting to local law enforcement if appropriate</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4">Photography and Recording</h2>
              <p>
                Professional photography and recording may take place during the event. By attending, 
                you consent to being photographed or recorded for promotional purposes. If you do 
                not wish to be photographed, please inform event staff who will ensure you are not 
                included in official photography.
              </p>
            </section>

            <section className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-600">
                This Code of Conduct is adapted from various community standards and best practices. 
                We are committed to providing a safe and welcoming environment for all participants.
              </p>
              <p className="text-sm text-gray-600 mt-2">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeOfConduct;